import { pushGtmEvent } from '@/src/analytics/gtm'
import { selectClevertapProfile } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getUserAuthValues } from '@/src/utils/auth'
import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { getFormattedDate } from '@/src/utils/date'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { parseMulticitySectors } from '@/src/utils/srp/searchUtils'
import { useLocale } from 'next-intl'
import { useMemo } from 'react'

// The search half of an SRP event (trip_type/airports/dates/pax/class). Supplied
// either from the current URL (page-view + book) or the modifier's NEW values (modify).
type SrpSearchCtx = {
    tripType?: string
    from?: string
    to?: string
    fromCity?: string
    toCity?: string
    departDate?: Date | string | null
    returnDate?: Date | string | null
    adults?: number
    children?: number
    infants?: number
    flightClass?: string
}

const useSrpGtmEvent = () => {
    const locale = useLocale()
    const { urlQueryObj } = useGetSearchParamsObj()

    const deviceType = getDeviceType()
    const platform = getPlatformName(deviceType)?.toLowerCase()
    const isSignedIn = isUserSignedIn()
    const { email } = getUserAuthValues() || {}
    const clevertapProfile = useAppSelector(selectClevertapProfile)
    // useSearchResults owns the OW/RT-vs-multicity API routing and exposes the shared
    // airports map (jsons.ap) we use for city names below.
    const { jsons, isMulticity } = useSearchResults()

    // Resolve the search context per trip type. For multicity the journey is collapsed to
    // first-sector departure → last-sector arrival. OW/RT keep from/to/depart/return semantics.
    const { from, to, flightClass, departDate, returnDate, adults, childs, infants, tripType } = useMemo(() => {
        const {
            from: searchFrom = '',
            to: searchTo = '',
            class: flightClass,
            depart_date,
            return_date,
            adults,
            childs,
            infants
        } = urlQueryObj || {}

        const travelers = {
            adults: Number(adults),
            childs: Number(childs),
            infants: Number(infants)
        }

        if (isMulticity) {
            const { sectors } = parseMulticitySectors(urlQueryObj || {})
            const mcFrom = sectors[0]?.from ?? ''
            const mcTo = sectors[sectors.length - 1]?.to ?? ''

            return {
                ...travelers,
                from: mcFrom,
                to: mcTo,
                flightClass,
                departDate: sectors[0]?.depart_date,
                returnDate: sectors[sectors.length - 1]?.depart_date,
                tripType: 'multi_city_trip'
            }
        }

        return {
            ...travelers,
            from: searchFrom,
            to: searchTo,
            flightClass,
            departDate: depart_date,
            returnDate: return_date,
            tripType: return_date ? 'round_trip' : 'one_way_trip'
        }
    }, [urlQueryObj, isMulticity])

    // City names come from the airports map (jsons.ap), which already reflects whichever
    // results query useSearchResults resolved for the current trip type.
    const ap = jsons?.ap

    // Common user/page attributes shared by every SRP event.
    const buildCommonBase = () => ({
        user_domain: window.location.hostname,
        platform,
        is_user_logged_in: isSignedIn ? 'yes' : 'no',
        user_language: locale,
        user_currency: getUserSelectedCurrencyCode()?.toLowerCase() || 'not_informed',
        product_category: 'flights',
        user_email: email ? email.toLowerCase() : 'not_informed',
        user_phone: clevertapProfile?.Phone || 'not_informed'
    })

    // Search context → the 6 search attributes (airports/dates combined). City falls back
    // to the airports map when the caller didn't supply it (e.g. an unchanged airport).
    const buildSearchFields = (ctx: SrpSearchCtx) => {
        const departure = ctx.departDate ? getFormattedDate(ctx.departDate, 'YYYY/MM/DD') : ''
        const returnFormatted = ctx.returnDate ? getFormattedDate(ctx.returnDate, 'YYYY/MM/DD') : ''
        const fromCity = ctx.fromCity || ap?.[ctx.from ?? '']?.c || ''
        const toCity = ctx.toCity || ap?.[ctx.to ?? '']?.c || ''
        return {
            trip_type: ctx.tripType,
            source_airport: `${fromCity} | ${ctx.from?.toLowerCase()}`,
            destination_airport: `${toCity} | ${ctx.to?.toLowerCase()}`,
            journey_dates: returnFormatted ? `${departure} | ${returnFormatted}` : departure,
            travellers_total_count: (ctx.adults || 0) + (ctx.children || 0) + (ctx.infants || 0),
            flight_class: ctx.flightClass?.toLowerCase()
        }
    }

    // Context derived from the current SRP URL — for the page-view + book events.
    const urlSearchCtx: SrpSearchCtx = {
        tripType,
        from,
        to,
        departDate,
        returnDate,
        adults,
        children: childs,
        infants,
        flightClass
    }

    // page_view_flight_search_results / click_book_flight (book adds airline_names).
    const pushEventToDataLayer = (payload: any = {}) => {
        const event = payload?.event
        const finalPayload =
            event === 'click_book_flight'
                ? {
                      event,
                      ...buildCommonBase(),
                      ...buildSearchFields(urlSearchCtx),
                      airline_names: payload.airline_names
                  }
                : { event, ...buildCommonBase(), ...buildSearchFields(urlSearchCtx) }

        pushGtmEvent(finalPayload)
    }

    // page_view_flight_Modify_search_results — fired at the modify-search click with the
    // NEW search values (passed by the caller), not the current URL.
    const pushModifySearchEvent = (searchCtx: SrpSearchCtx) => {
        pushGtmEvent({
            event: 'page_view_flight_Modify_search_results',
            ...buildCommonBase(),
            ...buildSearchFields(searchCtx)
        })
    }

    return {
        pushEventToDataLayer,
        pushModifySearchEvent
    }
}

export default useSrpGtmEvent
